import {useTrackingRef} from '@github-ui/use-tracking-ref'
import {createContext, memo, use, useCallback, useEffect, useMemo, useReducer, useState} from 'react'

import {defined, isEqualSets, xor} from '../../helpers/util'
import {getVisibleCells, isNonRedactedRow} from '../../hooks/tanstack-table/helpers/row-api'
import type {Cell, Row} from '../../hooks/tanstack-table/helpers/types'
import {useTableDeselectAllRows} from '../../hooks/tanstack-table/hooks/use-table-row-selection'
import {useTableInstance} from '../../hooks/tanstack-table/providers/table-provider'
import type {MemexItemModel} from '../../models/memex-item-model'
import {MAX_BULK_ITEMS} from '../../state-providers/column-values/use-bulk-update-item-column-value'
import {Resources} from '../../strings'
import {useGetVisibleRows} from './hooks/use-visible-rows'
import {isCellFocus, isSuspended, useTableNavigation} from './navigation'
import type {TableDataType} from './table-data-type'

/**
 * Delay in milliseconds before checking focus state after mousedown.
 * Allows focus to shift to the clicked cell before activating autoscroll.
 */
const MOUSE_DOWN_FOCUS_DELAY_MS = 100

type SelectionState = {
  readonly columnId: string | null
  /** Memex IDs of the selected rows (ie, `row.original.id`). */
  readonly rowIds: ReadonlySet<number>
  /** Reference row ID for range selection. */
  readonly rangeReferenceId: number | null
  /** `true` if more cells are selected than can be acted on. */
  readonly isMaximumExceeded: boolean
}

const SelectionState = {
  EMPTY: {columnId: null, rowIds: new Set(), rangeReferenceId: null, isMaximumExceeded: false} as SelectionState,
  isEqual: (a: SelectionState, b: SelectionState) =>
    a === b ||
    (a.columnId === b.columnId && a.rangeReferenceId === b.rangeReferenceId && isEqualSets(a.rowIds, b.rowIds)),
}

/**
 * - toggle: Unselected affected items will be selected, and selected affected items will be unselected. Other items
 *   will not change.
 * - replace: Existing selection will be discarded and replaced.
 * - add: Unselected affected items will be selected, and other items will not change.
 */
type SelectMode = 'toggle' | 'replace' | 'add'

export type SelectionMethods = {
  select: (cell: Pick<Cell<TableDataType>, 'column' | 'row'>, mode: SelectMode) => void
  selectRange: (cell: Pick<Cell<TableDataType>, 'column' | 'row'>, mode: SelectMode) => void
  expandSelectionUp: (cell: Pick<Cell<TableDataType>, 'column' | 'row'>) => void
  expandSelectionDown: (cell: Pick<Cell<TableDataType>, 'column' | 'row'>) => void
  clearSelection: () => void
  selectColumn: (columnId: string) => void
  replaceRange: (columnId: string, affectedRowIds: Array<number>) => void
}

const generateRange = (start: number, end: number) => {
  const min = Math.min(start, end)
  const range = Array.from({length: Math.abs(start - end) + 1}, (_, i) => min + i)
  // We always want the last element in the set to be the one the user clicked (to use as the lastAffectedIndex)
  return start <= end ? range : range.reverse()
}

type SelectionUpdate = {
  affectedRowIds?: Array<number> | ((state: SelectionState) => Array<number>)
  mode: SelectMode
  columnId: string | null
  keepRangeReference?: boolean
}

const defaultAffectedRowIds: Array<number> | ((state: SelectionState) => Array<number>) = []
const reduceSelectionUpdate = (
  state: SelectionState,
  {columnId, affectedRowIds = defaultAffectedRowIds, mode, keepRangeReference = false}: SelectionUpdate,
): SelectionState => {
  if (columnId === null) return SelectionState.EMPTY // short circuit for tiny perf gain

  // Start from empty if not in current column
  const initialState = state.columnId !== columnId ? SelectionState.EMPTY : state

  const resolvedAffectedRowIds = Array.isArray(affectedRowIds) ? affectedRowIds : affectedRowIds?.(initialState)

  // For range selection, don't update the reference index
  const rangeReferenceId = keepRangeReference
    ? state.rangeReferenceId
    : resolvedAffectedRowIds.at(-1) ?? state.rangeReferenceId

  let rowIds: Set<number>
  switch (mode) {
    case 'toggle':
      rowIds = xor(initialState.rowIds, new Set(resolvedAffectedRowIds))
      break
    case 'replace':
      rowIds = new Set(resolvedAffectedRowIds)
      break
    case 'add':
      rowIds = new Set([...initialState.rowIds, ...resolvedAffectedRowIds])
  }

  const result: SelectionState = {
    columnId,
    rowIds,
    rangeReferenceId,
    isMaximumExceeded: rowIds.size > MAX_BULK_ITEMS,
  }

  return SelectionState.isEqual(result, state) ? state : result
}

const SelectionStateContext = createContext<SelectionState>(SelectionState.EMPTY)

const SelectionMethodsContext = createContext<SelectionMethods | null>(null)

const SelectionAreCellsBulkSelectedContext = createContext<boolean | null>(null)

export const MouseHeldWithTableFocusContext = createContext<boolean>(false)

export const TableCellBulkSelectionProvider = memo<{children: React.ReactNode}>(
  function TableCellBulkSelectionProvider(props) {
    const [state, dispatch] = useReducer(reduceSelectionUpdate, SelectionState.EMPTY)
    const [isMouseHeld, setIsMouseHeld] = useState(false)

    // never changes, so it's fine to use as a dependency of the memo
    const table = useTableInstance()

    const focusRef = useTrackingRef(useTableNavigation().state.focus)

    const getRowIndex = useCallback(
      (rowId: number) => {
        const flatRows = table.getRowModel().flatRows
        return flatRows.filter(row => !row.getIsGrouped()).findIndex(row => row.original.id === rowId)
      },
      [table],
    )

    const getRowId = useCallback(
      (rowIndex: number) => table.getRowModel().flatRows.filter(row => !row.getIsGrouped())[rowIndex]?.original.id,
      [table],
    )

    const getFocusedCell = useCallback(() => {
      const focus = focusRef.current
      if (!focus || !isCellFocus(focus)) return undefined
      const rowsById = table.getRowModel().rowsById
      const row = rowsById[focus.details.y]
      if (!row) return undefined
      return getVisibleCells(row).find(
        cell =>
          // Optional chaining should be unecessary here - cell.column is not typed as optional - but we have run into
          // production errors because there are edge cases(race conditions ?) where it can be undefined
          cell.column?.id === focus.details.x,
      )
      // we use a tracking ref to avoid updating this every time focus changes
    }, [focusRef, table])

    const getRowRange = useCallback(
      (columnId: string, referenceRowId: number | null, targetRowId: number) => {
        const focusedCell = getFocusedCell()

        const targetIndex = getRowIndex(targetRowId)

        // reference row, fall back to focused row, then fall back to target row (just selecting one cell)
        const referenceIndex =
          referenceRowId !== null
            ? getRowIndex(referenceRowId)
            : focusedCell?.column.id === columnId
              ? getRowIndex(focusedCell.row.original.id) // row.index is incorrect in filtered views
              : targetIndex

        return generateRange(referenceIndex, targetIndex)
          .map(index => getRowId(index))
          .filter(defined)
      },
      [getRowIndex, getRowId, getFocusedCell],
    )

    const getVisibleRows = useGetVisibleRows()

    const getVisibleRowIds = useCallback(() => getVisibleRows().map(row => row.original.id), [getVisibleRows])

    /** Falls back to passed row ID if none found. */
    const getPreviousRow = useCallback(
      (rowId: number) => getRowId(getRowIndex(rowId) - 1) ?? rowId,
      [getRowIndex, getRowId],
    )

    /** Falls back to passed row ID if none found. */
    const getNextRow = useCallback(
      (rowId: number) => getRowId(getRowIndex(rowId) + 1) ?? rowId,
      [getRowIndex, getRowId],
    )

    const actions = useMemo<SelectionMethods>(
      () => ({
        select: (cell, mode) =>
          dispatch({
            columnId: cell.column.id,
            mode,
            affectedRowIds: ({columnId}) => {
              // When the user focuses a cell, it is not selected by default. But then when they meta+click, they expect
              // that focused cell to be included in the selection. We only do this when the selection is empty or we'd
              // end up toggling the focused cell, unselecting behind the user as they select things.
              const focusedCell = getFocusedCell()
              return (mode === 'toggle' || mode === 'add') && columnId === null && focusedCell
                ? [focusedCell.row.original.id, cell.row.original.id]
                : [cell.row.original.id]
            },
          }),
        selectRange: (cell, mode) =>
          dispatch({
            columnId: cell.column.id,
            mode,
            affectedRowIds: ({rangeReferenceId}) => getRowRange(cell.column.id, rangeReferenceId, cell.row.original.id),
            keepRangeReference: true,
          }),
        clearSelection: () => dispatch({columnId: null, mode: 'replace'}),
        expandSelectionUp: cell =>
          dispatch({
            columnId: cell.column.id,
            mode: 'add',
            affectedRowIds: [cell.row.original.id, getPreviousRow(cell.row.original.id)],
          }),
        replaceRange: (columnId: string, affectedRowIds: Array<number>) =>
          dispatch({
            columnId,
            mode: 'add',
            affectedRowIds,
          }),
        expandSelectionDown: cell =>
          dispatch({
            columnId: cell.column.id,
            mode: 'add',
            affectedRowIds: [cell.row.original.id, getNextRow(cell.row.original.id)],
          }),
        selectColumn: columnId => dispatch({affectedRowIds: getVisibleRowIds(), columnId, mode: 'replace'}),
      }),
      [getRowRange, getPreviousRow, getNextRow, getVisibleRowIds, getFocusedCell],
    )

    const deselectAllRows = useTableDeselectAllRows()
    const hasSelectedRows = state.rowIds.size > 0
    useEffect(() => {
      // Clear row selections when any cells get selected
      if (hasSelectedRows) deselectAllRows()
    }, [deselectAllRows, hasSelectedRows])

    useEffect(() => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null
      let isMouseCurrentlyDown = false

      const hasValidCellFocus = () => {
        return focusRef.current && isCellFocus(focusRef.current) && !isSuspended(focusRef.current)
      }

      const handleMouseDown = (e: MouseEvent) => {
        if (e.button === 0) {
          isMouseCurrentlyDown = true
          // Wait for focus to shift before activating autoscroll
          // If mouse is released before timeout, we ignore it
          timeoutId = setTimeout(() => {
            if (!isMouseCurrentlyDown) return

            if (!hasValidCellFocus()) return

            setIsMouseHeld(true)
          }, MOUSE_DOWN_FOCUS_DELAY_MS)
        }
      }

      const handleMouseUp = (e: MouseEvent) => {
        if (e.button === 0) {
          isMouseCurrentlyDown = false
          if (timeoutId !== null) {
            clearTimeout(timeoutId)
            timeoutId = null
          }
          setIsMouseHeld(false)
        }
      }

      const handleVisibilityChange = () => {
        // Reset state when window regains focus to handle cases where
        // mouse was released in a different window/tab
        if (document.visibilityState === 'visible') {
          isMouseCurrentlyDown = false
          if (timeoutId !== null) {
            clearTimeout(timeoutId)
            timeoutId = null
          }
          setIsMouseHeld(false)
        }
      }

      document.addEventListener('mousedown', handleMouseDown)
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('visibilitychange', handleVisibilityChange)

      return () => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId)
        }
        document.removeEventListener('mousedown', handleMouseDown)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }, [focusRef])

    return (
      <SelectionMethodsContext value={actions}>
        <SelectionStateContext value={state}>
          <SelectionAreCellsBulkSelectedContext value={state.rowIds.size > 0}>
            <MouseHeldWithTableFocusContext value={isMouseHeld}>
              {props.children}
              <span className="sr-only" aria-live="polite" aria-relevant="additions removals">
                {state.isMaximumExceeded && Resources.selectionMaximumExceeded(MAX_BULK_ITEMS)}
              </span>
            </MouseHeldWithTableFocusContext>
          </SelectionAreCellsBulkSelectedContext>
        </SelectionStateContext>
      </SelectionMethodsContext>
    )
  },
)

export const useTableCellBulkSelectionData = () => use(SelectionStateContext)

/** If `null`, bulk selection is disabled. */
export const useTableCellBulkSelectionActions = () => use(SelectionMethodsContext)

export const useIsCellBulkSelected = (cell?: Pick<Cell<TableDataType>, 'column' | 'row'>) => {
  const state = useTableCellBulkSelectionData()

  if (!cell) return false

  return state.columnId === cell.column.id && state.rowIds.has(cell.row.original.id)
}

export const useAreCellsBulkSelected = () => {
  return use(SelectionAreCellsBulkSelectedContext)
}

/** Get the selected row instances (in order of when they were first selected). */
export const useGetBulkSelectedRows = () => {
  const {rowIds} = useTableCellBulkSelectionData()
  const table = useTableInstance()

  return useCallback<() => Array<Row<MemexItemModel>>>(() => {
    const flatRows = table.getRowModel().flatRows
    return Array.from(rowIds)
      .map(id => flatRows.find(row => row.original.id === id))
      .filter(row => isNonRedactedRow(row))
  }, [rowIds, table])
}

export const useBulkSelectedColumn = () => {
  const state = useTableCellBulkSelectionData()
  const table = useTableInstance()
  const visibleColumns = table.getVisibleLeafColumns()
  return state.columnId !== null ? visibleColumns.find(col => col.id === state.columnId) : null
}

/**
 * Returns `true` when the user is holding the mouse down with cells selected.
 * This can be used to optimize rendering or change behavior during mouse+scroll interactions (used to bulk select cells).
 */
export const useIsMouseHeldWithTableFocus = () => {
  return use(MouseHeldWithTableFocusContext)
}

try{ SelectionStateContext.displayName ||= 'SelectionStateContext' } catch {}
try{ SelectionMethodsContext.displayName ||= 'SelectionMethodsContext' } catch {}
try{ SelectionAreCellsBulkSelectedContext.displayName ||= 'SelectionAreCellsBulkSelectedContext' } catch {}
try{ MouseHeldWithTableFocusContext.displayName ||= 'MouseHeldWithTableFocusContext' } catch {}
try{ TableCellBulkSelectionProvider.displayName ||= 'TableCellBulkSelectionProvider' } catch {}
try{ TableCellBulkSelectionProvider.displayName ||= 'TableCellBulkSelectionProvider' } catch {}